import {
  SEED_MARKETS,
  type Market,
  type SanctionsList,
  type SanctionsMatch,
  type SanctionsStatus,
  type User,
} from '../dataProvider/types';

/**
 * The sanctions-screening queue query/aggregation module (D-sanctions, Phase 7 surface 3) - the
 * ONE place matches are shaped for the worklist. Same seam idiom as the DSAR / chargebacks /
 * suspensions worklists: v1 aggregates client-side from the mock provider, every selector lives
 * here, and the real-data migration swaps this module for a server-side endpoint without the
 * surface changing.
 *
 * The matches are ENGINE-WRITTEN stand-ins (there is no real list and no admin run-a-screen
 * action); the queue is the review surface over them. The member's SANCTIONS STANDING is DERIVED
 * here (a member with an active confirmed match is sanctions-flagged), NOT a stored duplicate flag
 * on the user.
 *
 * REGION: OVERSIGHT-GLOBAL with a region FACET (the DSAR posture). Sanctions is cross-border, so
 * the queue spans markets by default; the optional `regionFilter` narrows by the match's stored
 * market by exact value.
 */

export interface SanctionsRow {
  id: number;
  userId: number;
  memberName: string;
  memberEmail: string;
  market: Market;
  matchedList: SanctionsList;
  matchScore: number;
  matchedName: string;
  screenedAt: string;
  sanctionsStatus: SanctionsStatus;
  reviewReason: string | null;
  reviewedAt: string | null;
  /** Derived: the member has an active confirmed match (so they are sanctions-flagged). */
  memberSanctionsFlagged: boolean;
}

export interface SanctionsQueueView {
  /** Open work (pending_review), urgency-sorted: highest match score first, then oldest. */
  pending: SanctionsRow[];
  /** History (cleared + confirmed), most recently reviewed first. */
  resolved: SanctionsRow[];
}

export interface SanctionsQueueInput {
  sanctionsMatches: SanctionsMatch[];
  users: User[];
}

export interface SanctionsQueueOptions {
  /** Exact-value market facet (uk / us), or null for the global view. */
  regionFilter?: Market | null;
}

/**
 * The set of members with an active confirmed match: the DERIVED sanctions standing. A member is
 * sanctions-flagged when any of their matches is confirmed, computed from the match collection,
 * never a stored flag on the user. Exported so a test can assert the derivation directly.
 */
export const flaggedMemberIds = (matches: SanctionsMatch[]): Set<number> =>
  new Set(
    matches
      .filter((m) => m.sanctionsStatus === 'confirmed')
      .map((m) => m.userId),
  );

/** Highest match score first, then oldest screened first, then id. */
const urgencyOrder = (a: SanctionsRow, b: SanctionsRow): number =>
  b.matchScore - a.matchScore ||
  a.screenedAt.localeCompare(b.screenedAt) ||
  a.id - b.id;

/** Resolved is history: most recently reviewed first, then id. */
const reviewedDesc = (a: SanctionsRow, b: SanctionsRow): number =>
  (b.reviewedAt ?? '').localeCompare(a.reviewedAt ?? '') || b.id - a.id;

export function buildSanctionsQueueView(
  input: SanctionsQueueInput,
  opts: SanctionsQueueOptions = {},
): SanctionsQueueView {
  const usersById = new Map(input.users.map((u) => [u.id, u]));
  const flagged = flaggedMemberIds(input.sanctionsMatches);

  const rows: SanctionsRow[] = input.sanctionsMatches
    .filter((m) => SEED_MARKETS.includes(m.market))
    .filter((m) => opts.regionFilter == null || m.market === opts.regionFilter)
    .map((m) => {
      const user = usersById.get(m.userId);
      return {
        id: m.id,
        userId: m.userId,
        memberName: user?.name ?? '',
        memberEmail: user?.email ?? '',
        market: m.market,
        matchedList: m.matchedList,
        matchScore: m.matchScore,
        matchedName: m.matchedName,
        screenedAt: m.screenedAt,
        sanctionsStatus: m.sanctionsStatus,
        reviewReason: m.reviewReason ?? null,
        reviewedAt: m.reviewedAt ?? null,
        memberSanctionsFlagged: flagged.has(m.userId),
      };
    });

  return {
    pending: rows
      .filter((r) => r.sanctionsStatus === 'pending_review')
      .sort(urgencyOrder),
    resolved: rows
      .filter((r) => r.sanctionsStatus !== 'pending_review')
      .sort(reviewedDesc),
  };
}
